import ExcelJS from "exceljs";
import moment from "moment";
import { extractWorkflowFieldsForExport } from "./shared-entregas-workflow";

const THIN_BORDER = {
    top: { style: "thin" },
    bottom: { style: "thin" },
    left: { style: "thin" },
    right: { style: "thin" },
};

function formatCoordenadasExcel(value) {
    const s = value == null ? "" : String(value).trim();
    if (!s || s.includes("undefined")) return "";
    return s;
}

function cellValue(value) {
    if (value == null) return "";
    if (typeof value === "string" && value.trim() === "") return "";
    return value;
}

/**
 * Download an ExcelJS buffer as .xlsx (same approach as grid.js).
 * @param {ArrayBuffer} buffer
 * @param {string} fileName
 */
export function sharedDownloadExcelBuffer(buffer, fileName = "reporte") {
    const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName.endsWith(".xlsx") ? fileName : `${fileName}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/**
 * Generic Excel export with title, date range, generation date, and styled grid.
 * @param {{
 *   reportName?: string,
 *   dateRangeLabel?: string,
 *   fileName?: string,
 *   sheetName?: string,
 *   columns: Array<{ dataField: string, caption?: string, width?: number }>,
 *   rows: Array<Record<string, unknown>>,
 * }} options
 */
export async function sharedExportExcelReport(options) {
    const {
        reportName = "Reporte",
        dateRangeLabel = "",
        fileName = "reporte",
        sheetName = "Reporte",
        columns = [],
        rows = [],
    } = options;

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(sheetName);

    const colCount = Math.max(columns.length, 1);
    const lastColLetter = worksheet.getColumn(colCount).letter;

    worksheet.mergeCells(`A1:${lastColLetter}1`);
    worksheet.getCell("A1").value = reportName;
    worksheet.getCell("A1").font = { bold: true, size: 14 };
    worksheet.getCell("A1").alignment = { horizontal: "center" };

    worksheet.mergeCells(`A2:${lastColLetter}2`);
    worksheet.getCell("A2").value = dateRangeLabel
        ? `Rango: ${dateRangeLabel}`
        : "";
    worksheet.getCell("A2").alignment = { horizontal: "center" };

    worksheet.mergeCells(`A3:${lastColLetter}3`);
    worksheet.getCell("A3").value = `Fecha de generación: ${moment().format(
        "DD/MM/YYYY HH:mm:ss"
    )}`;
    worksheet.getCell("A3").alignment = { horizontal: "center" };

    const headerRow = worksheet.getRow(5);
    columns.forEach((col, i) => {
        const cell = headerRow.getCell(i + 1);
        cell.value = col.caption || col.dataField;
        cell.font = { bold: true };
        cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFE0E0E0" },
        };
        cell.border = THIN_BORDER;
        cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    });

    rows.forEach((row, rIdx) => {
        const excelRow = worksheet.getRow(6 + rIdx);
        columns.forEach((col, cIdx) => {
            const cell = excelRow.getCell(cIdx + 1);
            cell.value = cellValue(row[col.dataField]);
            cell.border = THIN_BORDER;
            cell.alignment = { vertical: "top", wrapText: true };
        });
    });

    columns.forEach((col, i) => {
        let excelWidth;
        if (typeof col.width === "number" && col.width > 0) {
            excelWidth = Math.max(8, col.width / 7);
        } else {
            let maxLen = (col.caption || col.dataField || "").length;
            rows.forEach((row) => {
                const v = row[col.dataField];
                if (v != null) {
                    const s = String(v);
                    if (s.length > maxLen) maxLen = s.length;
                }
            });
            excelWidth = Math.min(50, Math.max(12, maxLen + 2));
        }
        worksheet.getColumn(i + 1).width = excelWidth;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    sharedDownloadExcelBuffer(buffer, fileName);
}

function getProductosList(item) {
    return Array.isArray(item?.productos) ? item.productos : [];
}

/**
 * Flatten delivery items: one Excel row per product (delivery cols repeated).
 * @param {Array<Record<string, unknown>>} items
 * @param {Record<string, string>} [workflowLabels] passed to shared-entregas-workflow
 */
export function flattenEntregasForExcel(items, workflowLabels = {}) {
    if (!Array.isArray(items)) return [];

    const flat = [];
    items.forEach((item, deliveryIndex) => {
        const workflowFields = extractWorkflowFieldsForExport(item, {
            ...workflowLabels,
            empty: workflowLabels.empty ?? "",
        });

        const base = {
            numeroFila: deliveryIndex + 1,
            labelPlaca: cellValue(item.labelPlaca ?? item.valuePlaca),
            numeroDocumento: cellValue(item.numeroDocumento),
            cliente: cellValue(item.cliente),
            geocerca: cellValue(item.geocerca),
            tipoServicio: cellValue(item.tipoServicio),
            estadoEntrega: workflowFields.estadoEntrega,
            subEstadoEntrega: workflowFields.subEstadoEntrega,
            finalizadoLabel: cellValue(item.finalizadoLabel),
            estadoPagoWorkflow: workflowFields.estadoPagoWorkflow,
            estadoPagoDetalle: workflowFields.estadoPagoDetalle,

            fechaEntregaProgramada: cellValue(item.fechaEntregaProgramada),
            fechaEntregaExcel: cellValue(item.fechaEntregaExcel),
            fechaCarguio: cellValue(item.fechaCarguio),
            fechaProgramacionSalida: cellValue(item.fechaProgramacionSalida),
            fechaProcesoWorkflow: cellValue(item.fechaProcesoWorkflow),

            coordenadasCliente: formatCoordenadasExcel(item.coordenadasCliente),
            coordenadasInicioAtencion: formatCoordenadasExcel(
                item.coordenadasInicioAtencion
            ),
            coordenadasFinAtencion: formatCoordenadasExcel(item.coordenadasFinAtencion),
            fechaHoraInicioAtencion: cellValue(item.fechaHoraInicioAtencion),
            fechaHoraFinAtencion: cellValue(item.fechaHoraFinAtencion),
            nombreConductor: cellValue(item.nombreConductor),
            almacen: cellValue(item.almacen),
        };

        const productos = getProductosList(item);
        if (productos.length === 0) {
            flat.push({
                ...base,
                codigoProducto: "",
                nombreProducto: "",
                unidadMedida: "",
                cantidad: "",
                peso: "",
                volumen: "",
                monto: "",
                moneda: "",
            });
            return;
        }

        productos.forEach((p, pIdx) => {
            flat.push({
                ...base,
                numeroFila: pIdx === 0 ? deliveryIndex + 1 : "",
                codigoProducto: cellValue(p.codigoProducto),
                nombreProducto: cellValue(String(p.nombreProducto ?? "").trim()),
                unidadMedida: cellValue(p.unidadMedida),
                cantidad: p.cantidad ?? "",
                peso: p.peso ?? "",
                volumen: p.volumen ?? "",
                monto: p.monto ?? "",
                moneda: cellValue(p.moneda),
            });
        });
    });

    return flat;
}

/**
 * Build column definitions for resumen entregas export.
 * @param {Record<string, string>} labels i18n captions
 */
export function buildEntregasExcelColumns(labels = {}) {
    const L = (key, fallback) => labels[key] ?? fallback;
    return [
        { dataField: "numeroFila", caption: "#", width: 40 },
        { dataField: "almacen", caption: L("almacen", "Almacen"), width: 120 },
        { dataField: "labelPlaca", caption: L("placa", "Placa"), width: 90 },
        { dataField: "numeroDocumento", caption: L("numeroDocumento", "N° Documento"), width: 110 },
        { dataField: "cliente", caption: L("cliente", "Cliente"), width: 220 },
        { dataField: "geocerca", caption: L("geocerca", "Geocerca"), width: 120 },
        { dataField: "tipoServicio", caption: L("tipoServicio", "Tipo servicio"), width: 100 },
        { dataField: "estadoEntrega", caption: L("estadoEntrega", "Estado"), width: 120 },
        { dataField: "subEstadoEntrega", caption: L("subEstadoEntrega", "Sub estado"), width: 120 },
        { dataField: "finalizadoLabel", caption: L("finalizado", "Finalizado"), width: 90 },
        { dataField: "estadoPagoWorkflow", caption: L("estadoPagoWorkflow", "Estado pago"), width: 110 },
        { dataField: "estadoPagoDetalle", caption: L("estadoPagoDetalle", "Detalle pago"), width: 200 },

        { dataField: "fechaEntregaProgramada", caption: L("fechaEntregaProgramada", "Fecha entrega programada"), width: 130 },
        { dataField: "fechaEntregaExcel", caption: L("fechaEntregaExcel", "Fecha entrega excel"), width: 130 },
        { dataField: "fechaCarguio", caption: L("fechaCarguio", "Fecha carguio"), width: 130 },
        { dataField: "fechaProgramacionSalida", caption: L("fechaProgramacionSalida", "Fecha programacion salida"), width: 130 },
        { dataField: "fechaProcesoWorkflow", caption: L("fechaProceso", "Fecha proceso"), width: 130 },

        { dataField: "coordenadasCliente", caption: L("coordCliente", "Coord. cliente"), width: 160 },
        // { dataField: "coordenadasInicioAtencion", caption: L("coordInicio", "Coord. inicio"), width: 160 },
        // { dataField: "coordenadasFinAtencion", caption: L("coordFin", "Coord. fin"), width: 160 },
        { dataField: "codigoProducto", caption: L("codigoProducto", "Código producto"), width: 110 },
        { dataField: "nombreProducto", caption: L("nombreProducto", "Nombre producto"), width: 280 },
        { dataField: "unidadMedida", caption: L("unidadMedida", "Unidad"), width: 70 },
        { dataField: "cantidad", caption: L("cantidad", "Cantidad"), width: 80 },
        { dataField: "peso", caption: L("peso", "Peso"), width: 70 },
        { dataField: "volumen", caption: L("volumen", "Volumen"), width: 70 },
        { dataField: "monto", caption: L("monto", "Monto"), width: 90 },
        { dataField: "moneda", caption: L("moneda", "Moneda"), width: 60 },
        { dataField: "fechaHoraInicioAtencion", caption: L("fechaHoraInicioAtencion", "Fecha/Hora inicio atención"), width: 130 },
        { dataField: "fechaHoraFinAtencion", caption: L("fechaHoraFinAtencion", "Fecha/Hora fin atención"), width: 130 },
        { dataField: "nombreConductor", caption: L("nombreConductor", "Nombre conductor"), width: 130 },
    ];
}

/**
 * Export resumen entregas rows (from TableEntregasTable filteredItems).
 * @param {Array<Record<string, unknown>>} items
 * @param {{
 *   reportName?: string,
 *   dateRangeLabel?: string,
 *   fileName?: string,
 *   labels?: Record<string, string>,
 *   workflowLabels?: Record<string, string>,
 * }} options
 */
export async function sharedExportEntregasExcel(items, options = {}) {
    const {
        reportName = "Resumen entregas",
        dateRangeLabel = "",
        fileName = `resumen-entregas-${moment().format("YYYY-MM-DD-HHmm")}`,
        labels = {},
        workflowLabels = {},
    } = options;

    const columns = buildEntregasExcelColumns(labels);
    const rows = flattenEntregasForExcel(items, workflowLabels);

    await sharedExportExcelReport({
        reportName,
        dateRangeLabel,
        fileName,
        sheetName: "Entregas",
        columns,
        rows,
    });
}
